import {
  useIsSingleProcessPage,
  useSummaryGroup,
  useVisibleProcesses,
} from "utils/customHooks/genericHooks";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import styled from "styled-components/macro";
import { SERVER_NAMES } from "components/Nav/Drawers/RightDrawer/FlightDebugger/SERVER_NAMES";
import { TREND_CHART_CONFIG } from "constants/customers";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { FONT_SIZE_EXTRA_SMALL, FONT_SIZE_SMALL } from "constants/constants";
import { COUNT_HEIGHT } from "../../TrendChartStyles";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { useProcessIdOnSingleProcessPage } from "components/CustomGridItems/LotInfoBar/lotInfoUtils";

export function TrendChartBarCount({ outputCount }: { outputCount: number }) {
  const outputLabel = useTrendChartOutputLabel();

  return (
    <TrendChartBarCountStyles
      data-server-names={[SERVER_NAMES.midgardDataDumpsTrendChart]}
    >
      {`${formatOutputCount(outputCount)} `}
      <div className="outputLabel">{outputLabel}</div>
    </TrendChartBarCountStyles>
  );
}
const TrendChartBarCountStyles = styled.div`
  flex-shrink: 0;
  height: ${COUNT_HEIGHT}px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  font-size: ${FONT_SIZE_SMALL}px;
  padding: 0 5px;
  box-sizing: border-box;
  line-height: 1em;
  margin-top: 0px;
  .outputLabel {
    font-size: ${FONT_SIZE_EXTRA_SMALL}px;
    line-height: 14px;
    height: 18px;
  }
`;

function formatOutputCount(outputCount: number): string {
  const hundredsRounded = Math.round((outputCount % 1000) / 100);
  const thousandsRounded = Math.round(outputCount / 1000);
  const thousandsFloored = Math.floor(outputCount / 1000);

  const outputCountFormatted =
    outputCount <= 9999
      ? Math.round(outputCount)
      : outputCount <= 999999
        ? `${
            [0, 10].includes(hundredsRounded)
              ? thousandsRounded
              : thousandsFloored + "." + hundredsRounded
          }k`
        : thousandsRounded + "k";
  return `${outputCountFormatted}`;
}

export function useTrendChartOutputLabel(): string {
  const processes = useVisibleProcesses();
  const ckey = useCustomerKey();
  const processId = processes.length === 1 ? processes[0].id : null;
  const { translate } = useFlightTranslation();
  const [flightConfig] = useFlightConfig();
  const summaryGroup = useSummaryGroup();
  const isSingleProcessPage = useIsSingleProcessPage();
  const labelByProcessGroup =
    summaryGroup && !isSingleProcessPage
      ? TREND_CHART_CONFIG[ckey]?.outputByProcessGroup?.[summaryGroup]?.label
      : null;
  const labelByProcess = processId
    ? (flightConfig.trendChart.outputLabelByProcess?.[processId] ??
      TREND_CHART_CONFIG[ckey]?.output[processId]?.label)
    : null;
  const labelOverall =
    flightConfig.trendChart.outputLabel ??
    (typeof TREND_CHART_CONFIG[ckey]?.output?.label === "string"
      ? TREND_CHART_CONFIG[ckey]?.output?.label
      : null);
  const label =
    labelByProcessGroup ?? labelByProcess ?? labelOverall ?? "parts";
  return translate(label);
}

export function useTrendChartOutputDataDumpName(): string {
  const ckey = useCustomerKey();
  const processId = useProcessIdOnSingleProcessPage();
  const [flightConfig] = useFlightConfig();

  const dataDumpNameByProcess = processId
    ? (flightConfig.trendChart.datadumpKeyByProcess?.[processId] ??
      TREND_CHART_CONFIG[ckey]?.output[processId]?.dataDumpName)
    : null;
  const dataDumpNameOverall =
    flightConfig.trendChart.datadumpKey ??
    (typeof TREND_CHART_CONFIG[ckey]?.output?.dataDumpName === "string"
      ? TREND_CHART_CONFIG[ckey]?.output?.dataDumpName
      : null);
  const dataDumpName = processId ? dataDumpNameByProcess : dataDumpNameOverall;
  return dataDumpName ?? "netOutput";
}
